/**
 * Appointment Hooks
 *
 * TanStack Query hooks for appointments, schedules, overrides, and slots
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type {
  CreateAppointmentData,
  AppointmentSearchFilters,
  RescheduleData,
  CreateProviderScheduleData,
  UpdateProviderScheduleData,
  ProviderScheduleSearchFilters,
  CreateScheduleOverrideData,
} from '@/domain/appointment/types'
import { useAuth, usePermiso } from '@/application/auth'
import * as service from './appointment.service'

// =============================================================================
// Query Keys
// =============================================================================

export const appointmentKeys = {
  all: ['appointments'] as const,
  lists: () => [...appointmentKeys.all, 'list'] as const,
  list: (filters: AppointmentSearchFilters) => [...appointmentKeys.lists(), filters] as const,
  details: () => [...appointmentKeys.all, 'detail'] as const,
  detail: (id: string) => [...appointmentKeys.details(), id] as const,
}

export const scheduleKeys = {
  all: ['schedules'] as const,
  lists: () => [...scheduleKeys.all, 'list'] as const,
  list: (filters: ProviderScheduleSearchFilters) => [...scheduleKeys.lists(), filters] as const,
  details: () => [...scheduleKeys.all, 'detail'] as const,
  detail: (id: string) => [...scheduleKeys.details(), id] as const,
}

export const overrideKeys = {
  all: ['overrides'] as const,
  byProvider: (providerId: string, dateFrom: string, dateTo: string) =>
    [...overrideKeys.all, providerId, dateFrom, dateTo] as const,
}

export const slotKeys = {
  all: ['slots'] as const,
  available: (providerId: string, modalityId: string, date: string) =>
    [...slotKeys.all, 'available', providerId, modalityId, date] as const,
  days: (providerId: string, modalityId: string, dateFrom: string, dateTo: string) =>
    [...slotKeys.all, 'days', providerId, modalityId, dateFrom, dateTo] as const,
}

// =============================================================================
// APPOINTMENT HOOKS
// =============================================================================

export function useAppointments(filters?: AppointmentSearchFilters) {
  const { isAuthenticated } = useAuth()
  // appointment:view:all, que es lo que exige la ruta. El radiologo y el
  // medico remitente tienen "Citas" en el menu y NO tienen ese permiso: la
  // pantalla se abria y la agenda respondia 403.
  const puede = usePermiso('appointment:view:all')
  return useQuery({
    queryKey: appointmentKeys.list(filters ?? {}),
    queryFn: () => service.getAppointments(filters),
    enabled: isAuthenticated && puede,
  })
}

export function useAppointment(id: string | undefined) {
  return useQuery({
    queryKey: appointmentKeys.detail(id ?? ''),
    queryFn: () => service.getAppointmentById(id!),
    enabled: !!id,
  })
}

export function useCreateAppointment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateAppointmentData) => service.createAppointment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() })
    },
  })
}

export function useScheduleAppointment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => service.scheduleAppointment(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: appointmentKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() })
    },
  })
}

export function useConfirmAppointment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => service.confirmAppointment(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: appointmentKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() })
    },
  })
}

export function useCheckInAppointment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => service.checkInAppointment(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: appointmentKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() })
    },
  })
}

export function useStartAppointment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => service.startAppointment(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: appointmentKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() })
    },
  })
}

export function useCompleteAppointment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => service.completeAppointment(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: appointmentKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() })
    },
  })
}

export function useCancelAppointment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      service.cancelAppointment(id, reason),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: appointmentKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() })
    },
  })
}

export function useMarkNoShow() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => service.markNoShow(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: appointmentKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() })
    },
  })
}

export function useRescheduleAppointment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: RescheduleData }) =>
      service.rescheduleAppointment(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: appointmentKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() })
    },
  })
}

export function useDeleteAppointment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => service.deleteAppointment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() })
    },
  })
}

// =============================================================================
// SCHEDULE HOOKS
// =============================================================================

export function useSchedules(filters?: ProviderScheduleSearchFilters) {
  const { isAuthenticated } = useAuth()
  return useQuery({
    queryKey: scheduleKeys.list(filters ?? {}),
    queryFn: () => service.getSchedules(filters),
    enabled: isAuthenticated,
  })
}

export function useSchedule(id: string | undefined) {
  return useQuery({
    queryKey: scheduleKeys.detail(id ?? ''),
    queryFn: () => service.getScheduleById(id!),
    enabled: !!id,
  })
}

export function useCreateSchedule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateProviderScheduleData) => service.createSchedule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.lists() })
    },
  })
}

export function useUpdateSchedule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProviderScheduleData }) =>
      service.updateSchedule(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: scheduleKeys.lists() })
    },
  })
}

export function useDeleteSchedule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => service.deleteSchedule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.lists() })
    },
  })
}

// =============================================================================
// OVERRIDE HOOKS
// =============================================================================

export function useOverrides(providerId: string | undefined, dateFrom: string, dateTo: string) {
  return useQuery({
    queryKey: overrideKeys.byProvider(providerId ?? '', dateFrom, dateTo),
    queryFn: () => service.getOverrides(providerId!, dateFrom, dateTo),
    enabled: !!providerId && !!dateFrom && !!dateTo,
  })
}

export function useCreateOverride() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateScheduleOverrideData) => service.createOverride(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: overrideKeys.all })
      queryClient.invalidateQueries({ queryKey: slotKeys.all })
    },
  })
}

export function useDeleteOverride() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => service.deleteOverride(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: overrideKeys.all })
      queryClient.invalidateQueries({ queryKey: slotKeys.all })
    },
  })
}

// =============================================================================
// SLOT HOOKS
// =============================================================================

export function useAvailableSlots(
  providerId: string | undefined,
  modalityId: string | undefined,
  date: string | undefined
) {
  return useQuery({
    queryKey: slotKeys.available(providerId ?? '', modalityId ?? '', date ?? ''),
    queryFn: () => service.getAvailableSlots(providerId!, modalityId!, date!),
    enabled: !!providerId && !!modalityId && !!date,
  })
}

export function useAvailableDays(
  providerId: string | undefined,
  modalityId: string | undefined,
  dateFrom: string | undefined,
  dateTo: string | undefined
) {
  return useQuery({
    queryKey: slotKeys.days(providerId ?? '', modalityId ?? '', dateFrom ?? '', dateTo ?? ''),
    queryFn: () => service.getAvailableDays(providerId!, modalityId!, dateFrom!, dateTo!),
    enabled: !!providerId && !!modalityId && !!dateFrom && !!dateTo,
  })
}

/**
 * Mi agenda para un rango de fechas.
 *
 * La clave incluye el rango para que cambiar de dia a semana no reutilice la
 * respuesta anterior.
 */
export function useMiAgenda(desde: string | undefined, hasta: string | undefined) {
  return useQuery({
    queryKey: ['mi-agenda', desde, hasta],
    queryFn: () => service.obtenerMiAgenda(desde!, hasta!),
    enabled: !!desde && !!hasta,
  })
}
